import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import { SidebarItem, UserType } from '../graphql/generated/graphql';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import allTopicsIcon from '../icons/non-prefilled/16x16/allTopics.svg';
import NewTopicModal from '../topic/modal/NewTopicModal';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import UIActions from '../ui/UIActions';
import ReorderMyTopicsModal from './ReorderMyTopicsModal';
import ReorderTopicsModal from './ReorderTopicsModal';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import styles from './SidebarTopics.module.scss';
import sortSidebarItems from './sortSidebarItems';
import useSidebarSettings from './useSidebarSettings';

interface TopicOverflowMenuProps {
  onReorderClick(): void;
}

const stopEvent: React.MouseEventHandler = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const TopicOverflowMenu = ({ onReorderClick }: TopicOverflowMenuProps) => {
  return (
    <button className={styles.menu} onClick={stopEvent} type="button">
      <OverflowMenu
        button={
          <div className={styles.menuButton}>
            <SVGIcon size={16}>{overflowMenuIcon}</SVGIcon>
          </div>
        }
      >
        <MenuItem icon={reorderIcon} onClick={onReorderClick}>
          Reorder topics
        </MenuItem>
      </OverflowMenu>
    </button>
  );
};

interface SidebarTopicsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  myTopics: AllTopicsTreeListTopic[] | null;
  allTopics: AllTopicsTreeListTopic[];
}

const SidebarTopics = ({
  activeTopics,
  expandedTopics,
  myTopics,
  allTopics,
}: SidebarTopicsProps) => {
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => state.session.user);
  const isGuest = currentUser.type === UserType.Guest;

  const { hiddenItems, myTopicIds } = useSidebarSettings();

  const createTopic = !isGuest ? (
    <button
      className={styles.addWrapper}
      onClick={(e) => {
        e.stopPropagation();
        dispatch(UIActions.openModal(NewTopicModal));
      }}
      type="button"
    >
      <Tooltip
        placement="top"
        popOverPortal={document.body}
        tooltip="Create a new topic"
        trigger="hover"
      >
        <div className={styles.add}>
          <SVGIcon size={9}>{addIcon}</SVGIcon>
        </div>
      </Tooltip>
    </button>
  ) : null;

  return (
    <>
      {!isGuest && myTopics && myTopics.length > 0 && (
        <SidebarSection
          name="myTopics"
          icon={topicIcon}
          title="My Topics"
          hidden={hiddenItems.has(SidebarItem.MyTopics)}
          hoverContent={
            <div className={styles.hoverContent}>
              {myTopics.length > 1 && (
                <TopicOverflowMenu
                  onReorderClick={() => {
                    dispatch(
                      UIActions.openModal(ReorderMyTopicsModal, {
                        myTopics: sortSidebarItems(myTopics, myTopicIds),
                      }),
                    );
                  }}
                />
              )}
              {createTopic}
            </div>
          }
        >
          <SidebarItems
            topics={myTopics}
            orderIds={myTopicIds}
            activeTopics={activeTopics}
            expandedTopics={expandedTopics}
            className={styles.list}
          />
        </SidebarSection>
      )}
      <SidebarSection
        name="allTopics"
        title="All Topics"
        icon={
          <SVGIcon className={styles.allTopicsIcon}>{allTopicsIcon}</SVGIcon>
        }
        hidden={hiddenItems.has(SidebarItem.AllTopics)}
        hoverContent={
          <div className={styles.hoverContent}>
            {currentUser.type === UserType.Admin && allTopics.length > 1 && (
              <TopicOverflowMenu
                onReorderClick={() => {
                  dispatch(
                    UIActions.openModal(ReorderTopicsModal, {
                      topics: allTopics,
                    }),
                  );
                }}
              />
            )}
            {createTopic}
          </div>
        }
      >
        <SidebarItems
          topics={allTopics}
          activeTopics={activeTopics}
          expandedTopics={expandedTopics}
          className={styles.list}
        />
      </SidebarSection>
    </>
  );
};

export default SidebarTopics;
